import { Link } from 'gatsby';
import { ButtonV2Props } from 'libs/growth-platform-brand-system-v2/src/components/ButtonV2';
import { Picto } from 'libs/growth-platform-brand-system-v2/src/components/Picto';
import { AsComponentProps } from 'libs/growth-platform-brand-system-v2/src/types/module';
import { createSchema } from 'morphism';
import React, { ReactNode } from 'react';
import { Subpath, linkResolver } from '../utils/linkResolver';

import { classNames } from 'libs/growth-platform-brand-system-v2/src/utils/style';

const allow: (string | undefined)[] = ['xs', 'sm'];

export const isSelfServeButMobile = (href: string, screenSize = '') => {
  return href?.includes('app.payfit.live') && allow.includes(screenSize);
};

const selectTitle = (
  title: ReactNode,
  titleResponsive: ReactNode,
  screenSize?: any,
  allowedScreenSizes: string[] = ['xs', 'sm'],
  forceTitleResponsive = false,
) => {
  if (
    (allowedScreenSizes.includes(screenSize) || forceTitleResponsive) &&
    titleResponsive
  ) {
    return titleResponsive;
  }
  return title;
};

export type DatoButtonProps = ButtonV2Props & {
  // isSelfServeButMobile?: boolean;
  __typename?: string;
  onClick?: any;
  isObfuscated?: boolean;
  rel?: string;
};

export const schemaDatoButton = createSchema<
  DatoButtonProps & { trackEvent?: string },
  any
>({
  __typename: '__typename',
  title: {
    path: [
      'btnTitle',
      'title',
      'label',
      'titleResponsive',
      'picto',
      'color',
      'screenSize',
      'isDisabled',
      'allowedScreenSizes',
      'forceTitleResponsive',
    ],
    fn: (
      {
        title,
        btnTitle,
        label,
        titleResponsive,
        picto,
        screenSize,
        color,
        isDisabled,
        allowedScreenSizes,
        forceTitleResponsive,
        trackEvent,
      }: any,
      source: any,
    ) => {
      const _title = btnTitle || title || label;

      title = selectTitle(
        _title,
        titleResponsive,
        screenSize,
        allowedScreenSizes,
        forceTitleResponsive,
      );

      const pictoName = picto?.[0]?.name;
      if (pictoName) {
        title = (
          <div className={'flex items-center'}>
            <Picto
              name={pictoName}
              variant={color === 'default' ? 'white' : undefined}
              disablePadding
              className={classNames('mr-8 w-6 h-6')}
            />
            {title}
          </div>
        );
      }
      return title;
    },
  },
  href: {
    path: ['url', 'externalUrl'],
    fn: (propertyValue: any, source: any) => {
      if (
        !propertyValue?.url &&
        !propertyValue?.externalUrl &&
        !source?.internalUrl
      ) {
        const title = source?.title || source?.btnTitle;
        return `#${title?.replace(/\W+/g, '-')}`;
      }
      if (source?.internalUrl) return;
      return propertyValue?.url || propertyValue?.externalUrl;
    },
  },
  to: {
    path: ['internalUrl', 'locale', 'urlParams', 'params'],
    fn: ({ internalUrl, locale, urlParams, params }: any, source: any) => {
      if (internalUrl) {
        const subpath = source?.isClientLink
          ? Subpath.Client
          : source?.isBlogArticle
            ? Subpath.BlogArticles
            : source?.internalUrl?.__typename === 'DatoCmsPillarPage'
              ? Subpath.Pillar
              : undefined;
        return linkResolver(
          internalUrl,
          locale,
          params || urlParams || '',
          subpath,
        );
      }
      return undefined;
    },
  },
  rel: {
    path: ['url', 'externalUrl'],
    fn: ({ url, externalUrl }: any) => {
      const href = url || externalUrl;
      if (!href) return;
      if (
        href?.startsWith('/') ||
        href?.startsWith('./') ||
        href?.startsWith('#')
      )
        return;
      if (!href?.includes('payfit.com')) return 'noopener noreferrer';
      return;
    },
  },
  className: {
    path: 'className',
    fn: (propertyValue: string, source: any) => {
      if (propertyValue) return propertyValue;
      if (source?.url || source?.internalUrl) return 'flex items-center';
      return '';
    },
  },
  as: {
    path: 'as',
    fn: (propertyValue: AsComponentProps, source: any) => {
      if (propertyValue) return propertyValue;
      if (source?.internalUrl) return Link;
      if (source?.url || source?.externalUrl) return 'a';
      if (source?.onClick) return 'button';
      return 'a';
    },
  },
  variant: {
    path: ['btnType', 'type', 'variant'],
    fn: ({ btnType, type, variant }: any) =>
      btnType || type || variant || 'minimal',
  },
  color: {
    path: 'color',
    fn: (propertyValue: any) =>
      ['default', 'invert'].includes(propertyValue) ? propertyValue : 'default',
  },
  role: 'role',
  tabIndex: 'tabIndex',
  isDisabled: 'isDisabled',
  isObfuscated: 'isObfuscated',
  trackEvent: 'trackEvent',
});

export const filterIsSelfServeButMobile = (
  propertieValue: any[],
): DatoButtonProps[] => {
  return propertieValue.reduce((acc: any, elt, index) => {
    if (!elt?.isSelfServeButMobile) {
      const { isSelfServeButMobile, ...cta } = elt;
      acc.push({ ...cta, index });
    }
    return acc;
  }, []);
};
